import { RefreshCw, BookOpen, AlertCircle } from 'lucide-react'
import { useEffect, useState } from 'react'
import { API_URL } from '../../config/api'
import './ProjectSummary.css'

interface SummaryData {
  id: number
  project_id: number
  content: string
  version: number
  generated_at: string
}

interface ProjectSummaryProps {
  projectId: number
}

export default function ProjectSummary({ projectId }: ProjectSummaryProps) {
  const [summary, setSummary] = useState<SummaryData | null>(null)
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    const loadSummary = async () => {
      try {
        const res = await fetch(`${API_URL}/api/research/projects/${projectId}/summary`)
        if (res.ok) {
          const data = await res.json()
          setSummary(data.summary as SummaryData);
          setError('');
        } else {
          setError('Özet bulunamadı')
        }
      } catch (err) {
        console.error('Summary load error:', err)
        setError('Özet yüklenirken hata oluştu')
      } finally {
        setLoading(false)
      }
    }
    void loadSummary()
  }, [projectId])

  const handleGenerate = async () => {
    setGenerating(true)
    setError('')
    try {
      const res = await fetch(`${API_URL}/api/research/projects/${projectId}/summary/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
      if (res.ok) {
        const data = await res.json()
        setSummary((data.summary || data) as SummaryData);
      } else {
        setError('Özet oluşturulamadı')
      }
    } catch (err) {
      console.error('Summary generate error:', err)
      setError('Özet oluştururken hata oluştu')
    } finally {
      setGenerating(false)
    }
  }

  if (loading) {
    return (
      <div className="summary-container">
        <div className="summary-skeleton">
          <div style={{ height: '20px', background: '#e2e8f0', borderRadius: '8px', marginBottom: '12px' }} />
          <div style={{ height: '60px', background: '#e2e8f0', borderRadius: '8px', marginBottom: '12px' }} />
          <div style={{ height: '40px', background: '#e2e8f0', borderRadius: '8px' }} />
        </div>
      </div>
    )
  }

  return (
    <div className="summary-container">
      {error && (
        <div className="summary-error">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {summary ? (
        <div className="summary-card">
          <div className="summary-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <BookOpen size={20} style={{ color: '#667eea' }} />
              <div>
                <h2>Living Summary</h2>
                <p className="summary-meta">
                  Version {summary.version} • {new Date(summary.generated_at).toLocaleDateString()}
                </p>
              </div>
            </div>
            <button className="btn-generate" onClick={() => { void handleGenerate(); }} disabled={generating}>
              <RefreshCw size={14} style={{ opacity: generating ? 0.5 : 1 }} />
              {generating ? 'Updating...' : 'Update Summary'}
            </button>
          </div>

          <div className="summary-content">
            <div className="summary-markdown">
              {summary.content.split('\n').map((line, i) => {
                if (line.startsWith('## ')) {
                  return <h3 key={i}>{line.slice(3)}</h3>
                }
                if (line.startsWith('# ')) {
                  return <h2 key={i}>{line.slice(2)}</h2>
                }
                if (line.startsWith('- ')) {
                  return <li key={i}>{line.slice(2)}</li>
                }
                if (line.trim() === '') {
                  return <br key={i} />
                }
                return <p key={i}>{line}</p>
              })}
            </div>
          </div>
        </div>
      ) : (
        <div className="summary-empty">
          <BookOpen size={48} />
          <h3>No Summary Yet</h3>
          <p>Generate a living summary from verified findings</p>
          <button className="btn-generate-primary" onClick={() => { void handleGenerate(); }} disabled={generating}>
            {generating ? 'Generating...' : 'Generate Summary'}
          </button>
        </div>
      )}
    </div>
  )
}
